import React from "react";
import { Dropdown } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../places/context/ThemeContext.jsx";
import LanguageSelector from "../../Landing/components/LanguageSelector/LanguageSelector.jsx";
import NavbarRadio from "./NavbarRadio.jsx";
import NavbarDateWeather from "./NavbarDateWeather.jsx";
import "./Navbar.css";
import "./NavbarAssistant.css";

const getTokenName = () => {
  const token =
    Cookies.get("assistantAccessToken") ||
    localStorage.getItem("assistantAccessToken") ||
    Cookies.get("assistantToken") ||
    localStorage.getItem("assistantToken");

  if (!token) return "";

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload?.name || payload?.fullName || "").trim();
  } catch {
    return "";
  }
};

export default function NavbarAssistant({ isMobile, onMobileMenuToggle }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  const assistantEmail = localStorage.getItem("assistantEmail") || t("assistantPages.navbar.defaultEmail");
  const assistantName =
    localStorage.getItem("assistantName") ||
    getTokenName() ||
    assistantEmail.split("@")[0] ||
    t("assistantPages.navbar.defaultName");
  const initials = assistantName.slice(0, 2).toUpperCase();

  const handleLogout = () => {
    ["assistantAccessToken", "assistantToken", "assistant_id", "assistantName", "assistantEmail"].forEach((key) => {
      Cookies.remove(key);
      localStorage.removeItem(key);
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    localStorage.removeItem("userRole");
    navigate("/LoginAssistant", { replace: true });
  };

  const profileDropdownOverlay = (
    <div className="navbar__profile-dropdown">
      <div className="navbar__profile-dropdown__menu">
        <div
          className="navbar__profile-dropdown__item navbar__profile-dropdown__item--logout"
          onClick={handleLogout}
        >
          <span>{t("auth.logout")}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="navbar-wrapper">
      <nav className="navbar navbar--unified">
        <div className="navbar__left">
          {isMobile ? (
            <button
              className="navbar__mobile-menu-btn"
              onClick={onMobileMenuToggle}
              aria-label={t("assistantPages.navbar.toggleMenu")}
            >
              <MenuOutlined className="navbar__mobile-menu-icon" />
            </button>
          ) : (
            <div className="navbar__user-identity" style={{ cursor: "pointer" }}>
              <div className="navbar__user-avatar-wrapper">
                <div className="navbar__user-avatar-placeholder navbar__doctor-initials-avatar">
                  {initials}
                </div>
              </div>
              <div className="navbar__user-info">
                <div className="navbar__user-name-row">
                  <span className="navbar__user-name">
                    {assistantName}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="navbar__right">
          <NavbarDateWeather />
          <LanguageSelector />
          <NavbarRadio />

          <div
            className="navbar__icon-button navbar__icon-button--theme"
            onClick={toggleTheme}
            role="button"
            tabIndex={0}
            aria-label={
              theme === "dark"
                ? t("assistantPages.navbar.switchToLight")
                : t("assistantPages.navbar.switchToDark")
            }
          >
            {theme === "dark" ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M21 12.79A9 9 0 0 1 12.79 3 7 7 0 1 0 21 12.79Z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="12" r="5" />
                <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <line x1="12" y1="2" x2="12" y2="5" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" />
                  <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
                  <line x1="2" y1="12" x2="5" y2="12" />
                  <line x1="19" y1="12" x2="22" y2="12" />
                  <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" />
                  <line x1="17.66" y1="6.34" x2="19.78" y2="4.22" />
                </g>
              </svg>
            )}
          </div>

          <Dropdown
            overlay={profileDropdownOverlay}
            placement="bottomRight"
            trigger={["click"]}
            overlayClassName="navbar__profile-dropdown-overlay"
            getPopupContainer={() => document.body}
          >
            <div className="navbar__profile-trigger" role="button" tabIndex={0}>
              <div className="navbar__profile-progress-avatar" style={{ "--profile-progress-angle": "360deg" }}>
                <div className="navbar__profile-progress-avatar-inner">
                  <div className="navbar__profile-progress-avatar-placeholder navbar__doctor-initials-avatar">
                    {initials}
                  </div>
                </div>
              </div>
              <span className="navbar__profile-trigger-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
          </Dropdown>
        </div>
      </nav>
    </div>
  );
}
